import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { IAutocompleteOption } from '../components/common/autocomplete.tsx';
import { IQuestionsExportSettingsFormData } from '../components/questions/questions-export-settings-form.tsx';
import { NavBarEnum } from './enums.ts';

export const truncateText = (
  text: string | null | undefined,
  maxLength: number,
): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  else return text.slice(0, maxLength) + '...';
};

type LexicalNode = {
  children?: LexicalNode[];
  equation?: string;
  format?: number;
  text?: string;
  type?: string;
};

export type CollapsedSegment =
  | { type: 'text'; value: string; format: number }
  | { type: 'math'; value: string };

const extractLexicalText = (node: LexicalNode | null | undefined): string => {
  if (!node) return '';

  if (typeof node.text === 'string') return node.text;

  if (typeof node.equation === 'string') return `$${node.equation}$`;

  if (node.type === 'linebreak') return '\n';

  if (!Array.isArray(node.children)) return '';

  return node.children
    .map((child) => extractLexicalText(child))
    .join(node.type === 'paragraph' ? '\n' : ' ');
};

const splitMathSegments = (value: string) => {
  const parts: Array<{ type: 'text' | 'math'; value: string }> = [];
  const mathRegex = /\$(.+?)\$/g;
  let lastIndex = 0;

  for (const match of value.matchAll(mathRegex)) {
    const matchIndex = match.index ?? 0;

    if (matchIndex > lastIndex) {
      parts.push({
        type: 'text',
        value: value.slice(lastIndex, matchIndex),
      });
    }

    parts.push({
      type: 'math',
      value: match[0],
    });

    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < value.length) {
    parts.push({
      type: 'text',
      value: value.slice(lastIndex),
    });
  }

  return parts;
};

const truncateMathSafe = (value: string, maxLength: number) => {
  const parts = splitMathSegments(value);

  if (parts.length === 0) {
    return truncateText(value, maxLength);
  }

  let remaining = maxLength;
  let result = '';

  for (const part of parts) {
    if (remaining <= 0) break;

    if (part.type === 'math') {
      const mathLength = part.value.replace(/\$/g, '').length;

      if (mathLength > remaining) {
        break;
      }

      result += part.value;
      remaining -= mathLength;
      continue;
    }

    const truncated = truncateText(part.value, remaining);

    result += truncated;
    remaining -= truncated.length;

    if (truncated.length < part.value.length) {
      break;
    }
  }

  return result.trim();
};

export const normalizeCollapsedQuestionText = (
  value: string | null | undefined,
): string => {
  const rawValue = value ?? '';
  let normalizedValue = rawValue;

  if (rawValue.startsWith('{"root":')) {
    try {
      const parsedValue = JSON.parse(rawValue) as { root?: LexicalNode };

      normalizedValue = extractLexicalText(parsedValue.root).trim();
    } catch {
      normalizedValue = rawValue;
    }
  }

  normalizedValue = normalizedValue
    .replace(/\$\$([\s\S]+?)\$\$/g, '$$$1$$')
    .replace(/\\\((.+?)\\\)/g, '$$$1$$')
    .replace(/\\\[(.+?)\\\]/g, '$$$1$$')
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    // Preserve underscores because SAT blanks often rely on their exact length.
    .replace(/(\*\*|\*|~~)(.*?)\1/g, '$2')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/<img[^>]*>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();

  return normalizedValue;
};

const extractLexicalSegments = (
  node: LexicalNode | null | undefined,
): CollapsedSegment[] => {
  if (!node) return [];

  if (typeof node.text === 'string') {
    return [{ type: 'text', value: node.text, format: node.format ?? 0 }];
  }

  if (typeof node.equation === 'string') {
    return [{ type: 'math', value: `$${node.equation}$` }];
  }

  if (node.type === 'linebreak') {
    return [{ type: 'text', value: ' ', format: 0 }];
  }

  if (!Array.isArray(node.children)) return [];

  const segments: CollapsedSegment[] = [];

  node.children.forEach((child, i) => {
    if (i > 0 && node.type === 'paragraph') {
      segments.push({ type: 'text', value: ' ', format: 0 });
    }

    segments.push(...extractLexicalSegments(child));
  });

  return segments;
};

const truncateSegments = (
  segments: CollapsedSegment[],
  maxLength: number,
): CollapsedSegment[] => {
  const result: CollapsedSegment[] = [];
  let remaining = maxLength;

  for (const seg of segments) {
    if (remaining <= 0) break;

    if (seg.type === 'math') {
      const mathLength = seg.value.replace(/\$/g, '').length;
      if (mathLength > remaining) break;
      result.push(seg);
      remaining -= mathLength;
      continue;
    }

    if (seg.value.length <= remaining) {
      result.push(seg);
      remaining -= seg.value.length;
    } else {
      result.push({
        type: 'text',
        value: seg.value.slice(0, remaining) + '...',
        format: seg.format,
      });
      remaining = 0;
    }
  }

  return result;
};

const skipSegments = (
  segments: CollapsedSegment[],
  skipLength: number,
): CollapsedSegment[] => {
  const result: CollapsedSegment[] = [];
  let remaining = skipLength;

  for (const seg of segments) {
    if (remaining <= 0) {
      result.push(seg);
      continue;
    }

    if (seg.type === 'math') {
      const mathLength = seg.value.replace(/\$/g, '').length;
      if (mathLength <= remaining) {
        remaining -= mathLength;
      } else {
        remaining = 0;
        result.push(seg);
      }
      continue;
    }

    if (seg.value.length <= remaining) {
      remaining -= seg.value.length;
    } else {
      result.push({ ...seg, value: seg.value.slice(remaining) });
      remaining = 0;
    }
  }

  return result;
};

const extractNormalizedSegments = (
  value: string | null | undefined,
): CollapsedSegment[] => {
  const rawValue = value ?? '';

  if (rawValue.startsWith('{"root":')) {
    try {
      const parsed = JSON.parse(rawValue) as { root?: LexicalNode };
      const segments = extractLexicalSegments(parsed.root);

      const merged: CollapsedSegment[] = [];
      for (const seg of segments) {
        const last = merged[merged.length - 1];
        if (
          seg.type === 'text' &&
          last?.type === 'text' &&
          last.format === seg.format
        ) {
          last.value += seg.value;
        } else {
          merged.push({ ...seg });
        }
      }

      return merged.map((seg) =>
        seg.type === 'text'
          ? { ...seg, value: seg.value.replace(/\s+/g, ' ') }
          : seg,
      );
    } catch {
      // fall through to plain text
    }
  }

  return parseHtmlSegments(rawValue);
};

export const getCollapsedSegments = (
  value: string | null | undefined,
  maxLength: number,
): CollapsedSegment[] => {
  return truncateSegments(extractNormalizedSegments(value), maxLength);
};

export const getCollapsedSegmentsContinuation = (
  value: string | null | undefined,
  skipLength: number,
  maxLength: number,
): CollapsedSegment[] => {
  const all = extractNormalizedSegments(value);
  const continuation = skipSegments(all, skipLength);
  return truncateSegments(continuation, maxLength);
};

const FORMAT_ITALIC = 2;
const FORMAT_UNDERLINE = 8;

const parseHtmlSegments = (html: string): CollapsedSegment[] => {
  // Normalize math notations
  let value = html
    .replace(/\$\$([\s\S]+?)\$\$/g, '$$$1$$')
    .replace(/\\\((.+?)\\\)/g, '$$$1$$')
    .replace(/\\\[(.+?)\\\]/g, '$$$1$$');

  // Remove images
  value = value
    .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
    .replace(/<img[^>]*>/gi, ' ');

  // Extract segments with formatting from <i>, <em>, <u> tags
  const segments: CollapsedSegment[] = [];
  const tagRegex = /<(i|em|u|b|strong)(?:\s[^>]*)?>|<\/(i|em|u|b|strong)>/gi;

  let lastIndex = 0;
  let currentFormat = 0;
  const formatStack: number[] = [];

  for (const match of value.matchAll(tagRegex)) {
    const matchIndex = match.index ?? 0;

    // Push text before this tag
    if (matchIndex > lastIndex) {
      const text = stripRemainingHtml(value.slice(lastIndex, matchIndex));
      if (text) segments.push({ type: 'text', value: text, format: currentFormat });
    }

    const closingTag = match[2];
    if (closingTag) {
      // Closing tag — pop format
      if (formatStack.length) {
        currentFormat = formatStack.pop()!;
      }
    } else {
      // Opening tag — push format
      const tag = match[1].toLowerCase();
      formatStack.push(currentFormat);
      if (tag === 'i' || tag === 'em') currentFormat |= FORMAT_ITALIC;
      if (tag === 'u') currentFormat |= FORMAT_UNDERLINE;
    }

    lastIndex = matchIndex + match[0].length;
  }

  // Remaining text after last tag
  if (lastIndex < value.length) {
    const text = stripRemainingHtml(value.slice(lastIndex));
    if (text) segments.push({ type: 'text', value: text, format: currentFormat });
  }

  if (segments.length === 0) {
    const text = stripRemainingHtml(value);
    if (text) segments.push({ type: 'text', value: text, format: 0 });
  }

  // Split math segments within text segments
  const withMath: CollapsedSegment[] = [];
  for (const seg of segments) {
    if (seg.type === 'math') {
      withMath.push(seg);
      continue;
    }
    const parts = splitMathSegments(seg.value);
    for (const p of parts) {
      if (p.type === 'math') {
        withMath.push({ type: 'math', value: p.value });
      } else {
        withMath.push({ type: 'text', value: p.value, format: seg.format });
      }
    }
  }

  // Merge adjacent same-format text segments and normalize whitespace
  const merged: CollapsedSegment[] = [];
  for (const seg of withMath) {
    const last = merged[merged.length - 1];
    if (seg.type === 'text' && last?.type === 'text' && last.format === seg.format) {
      last.value += seg.value;
    } else {
      merged.push({ ...seg });
    }
  }

  return merged
    .map((seg) =>
      seg.type === 'text'
        ? { ...seg, value: seg.value.replace(/\s+/g, ' ').trim() }
        : seg,
    )
    .filter((seg) => seg.type === 'math' || seg.value);
};

const stripRemainingHtml = (text: string): string =>
  text
    .replace(/<[^>]+>/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`{1,3}([^`]+)`{1,3}/g, '$1')
    .replace(/(\*\*|\*|~~)(.*?)\1/g, '$2')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/^[-*+]\s+/gm, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');

export const getCollapsedQuestionText = (
  value: string | null | undefined,
  maxLength: number,
): string => {
  return truncateMathSafe(normalizeCollapsedQuestionText(value), maxLength);
};

export const getCollapsedQuestionTextLines = (
  value: string | null | undefined,
  maxLengths: number[],
  options?: { appendEllipsis?: boolean },
): string[] => {
  const normalizedValue = normalizeCollapsedQuestionText(value);

  if (!normalizedValue) {
    return maxLengths.map(() => '');
  }

  const parts = splitMathSegments(normalizedValue);
  const lines = maxLengths.map(() => '');
  let lineIndex = 0;
  let remaining = maxLengths[0] ?? 0;
  let hasOverflow = false;

  for (const part of parts) {
    if (lineIndex >= maxLengths.length) {
      hasOverflow = true;
      break;
    }

    if (part.type === 'math') {
      const mathLength = part.value.replace(/\$/g, '').length;

      if (mathLength > remaining && lines[lineIndex]) {
        lineIndex += 1;
        remaining = maxLengths[lineIndex] ?? 0;
      }

      if (lineIndex >= maxLengths.length) {
        hasOverflow = true;
        break;
      }

      lines[lineIndex] += part.value;
      remaining -= mathLength;

      if (remaining <= 0) {
        lineIndex += 1;
        remaining = maxLengths[lineIndex] ?? 0;
      }

      continue;
    }

    let text = part.value;

    while (text && lineIndex < maxLengths.length) {
      if (remaining <= 0) {
        lineIndex += 1;
        remaining = maxLengths[lineIndex] ?? 0;
        continue;
      }

      const chunk = text.slice(0, remaining);
      lines[lineIndex] += chunk;
      text = text.slice(chunk.length);
      remaining -= chunk.length;

      if (remaining <= 0) {
        lineIndex += 1;
        remaining = maxLengths[lineIndex] ?? 0;
      }
    }

    if (text) {
      hasOverflow = true;
      break;
    }
  }

  const trimmedLines = lines.map((line) => line.trim());
  const appendEllipsis = options?.appendEllipsis ?? true;

  if (hasOverflow && appendEllipsis) {
    for (let i = trimmedLines.length - 1; i >= 0; i -= 1) {
      if (!trimmedLines[i]) continue;
      trimmedLines[i] = truncateText(trimmedLines[i], maxLengths[i] ?? 0);

      if (!trimmedLines[i].endsWith('...')) {
        trimmedLines[i] = `${trimmedLines[i]}...`;
      }

      break;
    }
  }

  return trimmedLines;
};

export const getDateForInput = (
  date: Date | undefined | null,
): string | undefined => {
  if (!date) return undefined;
  const temp = new Date(date);
  const year = temp.getFullYear();
  const month = (temp.getMonth() + 1).toString().padStart(2, '0');
  const day = temp.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * transform Date in time string like 00:00
 * @param date
 */
export const getTimeForInput = (date: Date | undefined | null) => {
  if (!date) return undefined;
  const temp = new Date(date);
  const hours = String(temp.getHours()).padStart(2, '0');
  const minutes = String(temp.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
};

export const getDateString = (dateServer: Date | undefined | null): string => {
  if (!dateServer) return '';
  return new Date(dateServer).toLocaleDateString('en-US');
};

export const orderToLetter = (order: number) => {
  if (order < 0 || order > 25) throw 'Invalid order index';
  return String.fromCharCode(65 + order);
};

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs));

export const getAutocompleteTitle = (
  option: IAutocompleteOption | undefined,
) => {
  if (!option) return undefined;
  return typeof option === 'string' ? option : option.title;
};

export const getAutocompleteKey = (option: IAutocompleteOption | undefined) => {
  if (!option) return undefined;
  return typeof option === 'string' ? option : option.key;
};

export const createXlsxExportUrl = (
  type: 'quiz' | 'test',
  id: number,
  attempts: number[],
): string => {
  const searchParams = new URLSearchParams({
    id: id.toString(),
    attemptIds: attempts.join(','),
  });
  return `${import.meta.env.VITE_API_URL}/${type}/xlsx?${searchParams}`;
};

export const createPdfExportUrl = (
  type: 'quiz' | 'test',
  params: IQuestionsExportSettingsFormData & {
    id: string;
    moduleIds?: string;
  },
): string => {
  const searchParams = new URLSearchParams(
    Object.entries(params).map(([key, value]) => [key, value.toString()]),
  );
  return `${import.meta.env.VITE_API_URL}/${type}/pdf?${searchParams}`;
};

export const browserWatcher = (browser: NavBarEnum) => {
  const isQuestionsBrowser = browser === NavBarEnum.QUESTIONS_BROWSER;
  const isTestsBrowser = browser === NavBarEnum.TESTS_BROWSER;
  const isQuizzesBrowser = browser === NavBarEnum.QUIZZES_BROWSER;
  const isCoursesBrowser = browser === NavBarEnum.COURSES_BROWSER;
  const isUsersBrowser = browser === NavBarEnum.USERS_BROWSER;
  const isAssetsBrowser = browser === NavBarEnum.ASSETS_BROWSER;
  const isScriptsBrowser = browser === NavBarEnum.SCRIPTS_BROWSER;
  const isQuestionsReports = browser === NavBarEnum.QUESTIONS_REPORTS;
  const isTeacherAssets = browser === NavBarEnum.TEACHER_ASSETS;
  const isAnswersView = browser === NavBarEnum.ANSWERS_VIEW;

  return {
    isQuestionsBrowser,
    isTestsBrowser,
    isQuizzesBrowser,
    isCoursesBrowser,
    isUsersBrowser,
    isAssetsBrowser,
    isScriptsBrowser,
    isQuestionsReports,
    isTeacherAssets,
    isAnswersView,
  };
};
